import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import axios from 'axios'
import { useLanguage } from '../context/LanguageContext'
import { useAuth } from '../context/AuthContext'
import { Button } from '../components/ui/button'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card'
import { Label } from '../components/ui/label'
import { Textarea } from '../components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select'
import { toast } from 'sonner'
import { ArrowLeft, Star, MessageSquare, Bug, Lightbulb, Sparkles, HelpCircle, Smartphone, Globe, RefreshCw, Send, CheckCircle, CreditCard, CircleDot } from 'lucide-react'

const API = `${process.env.REACT_APP_BACKEND_URL}/api`

export default function FeedbackPage() {
  const navigate = useNavigate()
  const { locale, t } = useLanguage()
  const { token } = useAuth()
  const isSpanish = locale === 'es-MX'
  
  const [rating, setRating] = useState(0)
  const [hoveredRating, setHoveredRating] = useState(0)
  const [category, setCategory] = useState('')
  const [message, setMessage] = useState('')
  const [context, setContext] = useState('')
  const [preferredUsage, setPreferredUsage] = useState('')
  const [subscriptionInterest, setSubscriptionInterest] = useState('')
  const [subscriptionComment, setSubscriptionComment] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)

  const categories = [
    { value: 'bug', label: isSpanish ? 'Bug' : 'Bug', icon: Bug, color: 'text-red-500' },
    { value: 'improvement', label: isSpanish ? 'Mejora' : 'Improvement', icon: Sparkles, color: 'text-blue-500' },
    { value: 'idea', label: isSpanish ? 'Idea' : 'Idea', icon: Lightbulb, color: 'text-amber-500' },
    { value: 'general', label: isSpanish ? 'General' : 'General', icon: HelpCircle, color: 'text-gray-500' },
  ]

  const usageOptions = [
    { value: 'app', label: isSpanish ? 'Como app instalada en el teléfono' : 'As an installed app on my phone', icon: Smartphone },
    { value: 'web', label: isSpanish ? 'Desde navegador web' : 'From web browser', icon: Globe },
    { value: 'both', label: isSpanish ? 'Ambos me funcionan' : 'Both work for me', icon: RefreshCw },
  ]

  const subscriptionOptions = [
    { value: 'free', label: 'Prefiero que exista una versión gratuita con funciones básicas' },
    { value: 'mxn_49', label: '$49 MXN / mes' },
    { value: 'mxn_99', label: '$99 MXN / mes' },
    { value: 'mxn_199', label: '$199 MXN / mes' },
    { value: 'mxn_299_plus', label: '$299 MXN o más / mes' },
  ]

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (rating === 0) {
      toast.error(isSpanish ? 'Por favor selecciona una calificación' : 'Please select a rating')
      return
    }
    if (!category) {
      toast.error(isSpanish ? 'Por favor selecciona un tipo de feedback' : 'Please select a feedback type')
      return
    }
    if (!message.trim()) {
      toast.error(isSpanish ? 'Por favor escribe tu comentario' : 'Please write your comment')
      return
    }
    if (!preferredUsage) {
      toast.error(isSpanish ? 'Por favor selecciona cómo preferirías usar KaizenOS' : 'Please select how you prefer to use KaizenOS')
      return
    }

    setSubmitting(true)
    
    try {
      await axios.post(`${API}/feedback`, {
        rating,
        category,
        message: message.trim(),
        preferred_usage: preferredUsage,
        context: context.trim() || null,
        subscription_interest: subscriptionInterest || null,
        subscription_comment: subscriptionComment.trim() || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      })
      
      setSubmitted(true)
    } catch (error) {
      console.error('Error submitting feedback:', error)
      toast.error(error.response?.data?.detail || (isSpanish ? 'Error al enviar feedback' : 'Error submitting feedback'))
    } finally {
      setSubmitting(false)
    }
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-background p-4">
        <div className="max-w-lg mx-auto pt-20">
          <Card className="text-center">
            <CardContent className="pt-10 pb-10">
              <div className="w-20 h-20 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-emerald-500" />
              </div>
              <h2 className="text-2xl font-bold mb-2">
                {isSpanish ? '¡Gracias por tu opinión!' : 'Thank you for your feedback!'}
              </h2>
              <p className="text-muted-foreground mb-8">
                {isSpanish 
                  ? 'Nos ayuda a mejorar KaizenOS para ti y todos los usuarios.' 
                  : 'It helps us improve KaizenOS for you and all users.'}
              </p>
              <Button onClick={() => navigate(-1)} className="w-full">
                {isSpanish ? 'Volver' : 'Go back'}
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b">
        <div className="flex items-center gap-3 p-4 max-w-lg mx-auto">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="back-btn">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-lg font-semibold">
              {isSpanish ? 'Enviar Feedback' : 'Send Feedback'}
            </h1>
            <p className="text-xs text-muted-foreground">
              {isSpanish ? 'Tu opinión nos ayuda a mejorar' : 'Your opinion helps us improve'}
            </p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-lg mx-auto p-4 pb-24">
        <form onSubmit={handleSubmit} className="space-y-6">
          
          {/* Rating */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Star className="w-4 h-4" />
                {isSpanish ? 'Calificación' : 'Rating'}
              </CardTitle>
              <CardDescription>
                {isSpanish ? '¿Cómo calificarías tu experiencia?' : 'How would you rate your experience?'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex justify-center gap-2">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    onClick={() => setRating(star)}
                    onMouseEnter={() => setHoveredRating(star)}
                    onMouseLeave={() => setHoveredRating(0)}
                    className="p-1 transition-transform hover:scale-110"
                    data-testid={`rating-star-${star}`}
                  >
                    <Star 
                      className={`w-10 h-10 transition-colors ${
                        star <= (hoveredRating || rating) 
                          ? 'fill-amber-400 text-amber-400' 
                          : 'text-muted-foreground/30'
                      }`} 
                    />
                  </button>
                ))}
              </div>
              {rating > 0 && (
                <p className="text-center text-sm text-muted-foreground mt-2">
                  {rating === 1 && (isSpanish ? 'Muy malo' : 'Very bad')}
                  {rating === 2 && (isSpanish ? 'Malo' : 'Bad')}
                  {rating === 3 && (isSpanish ? 'Regular' : 'Average')}
                  {rating === 4 && (isSpanish ? 'Bueno' : 'Good')}
                  {rating === 5 && (isSpanish ? 'Excelente' : 'Excellent')}
                </p>
              )}
            </CardContent>
          </Card>

          {/* Category */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <MessageSquare className="w-4 h-4" />
                {isSpanish ? 'Tipo de feedback' : 'Feedback type'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-2">
                {categories.map((cat) => {
                  const Icon = cat.icon
                  return (
                    <button
                      key={cat.value}
                      type="button"
                      onClick={() => setCategory(cat.value)}
                      className={`p-4 rounded-xl border-2 transition-all flex flex-col items-center gap-2 ${
                        category === cat.value 
                          ? 'border-primary bg-primary/5' 
                          : 'border-transparent bg-muted/50 hover:bg-muted'
                      }`}
                      data-testid={`category-${cat.value}`}
                    >
                      <Icon className={`w-6 h-6 ${cat.color}`} />
                      <span className="text-sm font-medium">{cat.label}</span>
                    </button>
                  )
                })}
              </div>
            </CardContent>
          </Card>

          {/* Message */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">
                {isSpanish ? 'Tu comentario' : 'Your comment'}
              </CardTitle>
              <CardDescription>
                {isSpanish 
                  ? '¿Qué podríamos mejorar en KaizenOS? ¿Encontraste algún problema o tienes una idea?' 
                  : 'What could we improve in KaizenOS? Did you find a problem or have an idea?'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder={isSpanish 
                  ? 'Escribe tu sugerencia, problema o idea aquí...' 
                  : 'Write your suggestion, problem or idea here...'}
                rows={4}
                maxLength={2000}
                data-testid="feedback-message"
              />
              <p className="text-xs text-muted-foreground mt-2 text-right">
                {message.length}/2000
              </p>
            </CardContent>
          </Card>

          {/* Context - What were you trying to do */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">
                {isSpanish ? '¿Qué estabas intentando hacer?' : 'What were you trying to do?'}
              </CardTitle>
              <CardDescription>
                {isSpanish ? 'Opcional - Nos ayuda a entender mejor el contexto' : 'Optional - Helps us understand the context better'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Textarea
                value={context}
                onChange={(e) => setContext(e.target.value)}
                placeholder={isSpanish 
                  ? 'Ej: Estaba intentando agregar una transacción cuando...' 
                  : 'E.g.: I was trying to add a transaction when...'}
                rows={2}
                maxLength={500}
                data-testid="feedback-context"
              />
              <p className="text-xs text-muted-foreground mt-2 text-right">
                {context.length}/500
              </p>
            </CardContent>
          </Card>

          {/* Preferred Usage */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">
                {isSpanish ? '¿Cómo preferirías usar KaizenOS?' : 'How would you prefer to use KaizenOS?'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {usageOptions.map((option) => {
                  const Icon = option.icon
                  return (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => setPreferredUsage(option.value)}
                      className={`w-full p-4 rounded-xl border-2 transition-all flex items-center gap-3 ${
                        preferredUsage === option.value 
                          ? 'border-primary bg-primary/5' 
                          : 'border-transparent bg-muted/50 hover:bg-muted'
                      }`}
                      data-testid={`usage-${option.value}`}
                    >
                      <Icon className="w-5 h-5 text-primary" />
                      <span className="text-sm">{option.label}</span>
                    </button>
                  )
                })}
              </div>
            </CardContent>
          </Card>

          {/* Subscription Interest */}
          <Card className="border-primary/20 bg-primary/5">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <CreditCard className="w-4 h-4 text-primary" />
                Modelo de suscripción
              </CardTitle>
              <CardDescription className="text-sm leading-relaxed">
                Estamos evaluando cuál podría ser el mejor modelo de suscripción para KaizenOS en el futuro.
                <br /><br />
                Si KaizenOS realmente te aporta valor en tu vida diaria, ¿cuánto estarías dispuesto a pagar al mes por usarlo?
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {subscriptionOptions.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setSubscriptionInterest(option.value)}
                    className={`w-full p-4 rounded-xl border-2 transition-all flex items-center gap-3 text-left ${
                      subscriptionInterest === option.value 
                        ? 'border-primary bg-background' 
                        : 'border-transparent bg-background/50 hover:bg-background'
                    }`}
                    data-testid={`subscription-${option.value}`}
                  >
                    <CircleDot className={`w-4 h-4 flex-shrink-0 ${
                      subscriptionInterest === option.value 
                        ? 'text-primary' 
                        : 'text-muted-foreground/50'
                    }`} />
                    <span className="text-sm">{option.label}</span>
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Subscription Comment */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">
                ¿Qué funcionalidades te harían pagar una suscripción por KaizenOS?
              </CardTitle>
              <CardDescription>
                Opcional - Nos ayuda a priorizar desarrollo
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Textarea
                value={subscriptionComment}
                onChange={(e) => setSubscriptionComment(e.target.value)}
                placeholder="Ej: Sincronización con calendario, más opciones de análisis financiero, reportes avanzados..."
                rows={3}
                maxLength={1000}
                data-testid="subscription-comment"
              />
              <p className="text-xs text-muted-foreground mt-2 text-right">
                {subscriptionComment.length}/1000
              </p>
            </CardContent>
          </Card>

          {/* Submit Button */}
          <Button 
            type="submit" 
            className="w-full h-12 text-base gap-2" 
            disabled={submitting}
            data-testid="submit-feedback-btn"
          >
            {submitting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-current border-t-transparent" />
                {isSpanish ? 'Enviando...' : 'Sending...'}
              </>
            ) : (
              <>
                <Send className="w-4 h-4" />
                {isSpanish ? 'Enviar feedback' : 'Send feedback'}
              </>
            )}
          </Button>
        </form>
      </div>
    </div>
  )
}
